/**
 * Validates a hex color string (3, 4, 6, or 8 digits with optional #).
 */
export const isValidHex = (hex: string): boolean =>
  /^#?([0-9A-Fa-f]{3}|[0-9A-Fa-f]{4}|[0-9A-Fa-f]{6}|[0-9A-Fa-f]{8})$/.test(hex);

/**
 * Parses a hex color string into RGBA components.
 * Returns { r: 0, g: 0, b: 0, a: 1 } for invalid inputs.
 */
export const hexToRgb = (
  hex: string
): { r: number; g: number; b: number; a: number } => {
  const raw = hex.replace(/^#/, "");

  if (
    !/^([0-9A-Fa-f]{3}|[0-9A-Fa-f]{4}|[0-9A-Fa-f]{6}|[0-9A-Fa-f]{8})$/.test(raw)
  ) {
    return { r: 0, g: 0, b: 0, a: 1 };
  }

  let r: number,
    g: number,
    b: number,
    a = 255;
  if (raw.length === 3 || raw.length === 4) {
    r = parseInt(raw[0] + raw[0], 16);
    g = parseInt(raw[1] + raw[1], 16);
    b = parseInt(raw[2] + raw[2], 16);
    if (raw.length === 4) a = parseInt(raw[3] + raw[3], 16);
  } else {
    r = parseInt(raw.slice(0, 2), 16);
    g = parseInt(raw.slice(2, 4), 16);
    b = parseInt(raw.slice(4, 6), 16);
    if (raw.length === 8) a = parseInt(raw.slice(6, 8), 16);
  }

  return { r, g, b, a: Math.round((a / 255) * 100) / 100 };
};

/**
 * Converts a hex color to an 8-digit hex string (#RRGGBBAA).
 */
export const hexToHex8 = (hex: string): string => {
  const { r, g, b, a } = hexToRgb(hex);
  const alpha = Math.round(a * 255);

  return `#${[r, g, b, alpha].map(channel => channel.toString(16).padStart(2, "0")).join("")}`;
};

/**
 * Converts a color value to a normalized { hex, rgb } object.
 * Handles transparency and validity checks.
 */
export const getColor = (
  colorValue: string,
  showTransparencyControl: boolean
): { hex: string; rgb: { r: number; g: number; b: number; a: number } } => {
  if (!isValidHex(colorValue)) {
    return { hex: colorValue, rgb: { r: 0, g: 0, b: 0, a: 1 } };
  }

  const rgb = hexToRgb(colorValue);

  if (rgb.a === 0) {
    return { hex: colorValue, rgb };
  }

  const hex = showTransparencyControl
    ? hexToHex8(colorValue)
    : `#${colorValue.replace(/^#/, "").slice(0, 6)}`;

  return { hex, rgb: hexToRgb(hex) };
};
